import {
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { decideMapping, listIntegrationMappings } from '../../api/business/endpoints';
import type { IntegrationLink, IntegrationMapping, MappingStatus } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate } from '../../components/format';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { MappingDialog } from './MappingDialog';

type MappingSortKey = 'shopName' | 'code' | 'status' | 'productName';

const VALUE_OF: Record<MappingSortKey, (mapping: IntegrationMapping) => string | null> = {
  shopName: (mapping) => mapping.shopName,
  code: (mapping) => mapping.shopEan ?? mapping.shopReference,
  status: (mapping) => mapping.status,
  productName: (mapping) => mapping.productName,
};

/** Every item the shop reported and what it is in the app — the whole list, sorted in the browser. */
export const IntegrationMappingsTable = ({
  link,
  canManage,
}: {
  link: IntegrationLink;
  canManage: boolean;
}) => {
  const { language, t } = useLanguage();
  const [status, setStatus] = useState<MappingStatus | ''>(link.unmatched > 0 ? 'UNMATCHED' : '');
  const [term, setTerm] = useState('');
  const [mapping, setMapping] = useState<IntegrationMapping | null>(null);
  const sort = useSort<MappingSortKey>('shopName');
  const query = useQuery({
    queryKey: ['integration-mappings', link.id],
    queryFn: () => listIntegrationMappings(link.companyId, link.id),
  });
  const invalidates = ['integration-mappings', 'integration-link', 'integration-links'];
  const decide = useAppMutation(decideMapping, invalidates, () => setMapping(null));

  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    (query.data ?? []).filter(
      (one) =>
        (status === '' || one.status === status) &&
        (needle === '' ||
          [one.shopName, one.shopEan, one.shopReference, one.productName].some((value) =>
            value?.toLocaleLowerCase('sk').includes(needle),
          )),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );

  return (
    <Paper variant="outlined">
      <ErrorNotice
        error={query.error ?? (mapping === null ? decide.error : null)}
        onRetry={() => void query.refetch()}
      />
      <TableFilters>
        <TextField
          size="small"
          label={t('common.search')}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <TextField
          select
          size="small"
          label={t('field.status')}
          value={status}
          onChange={(e) => setStatus(e.target.value as MappingStatus | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {(['MATCHED', 'UNMATCHED', 'IGNORED'] as const).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`mappingStatus.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableCell sort={sort} column="shopName">
                {t('mapping.shopName')}
              </SortableCell>
              <SortableCell sort={sort} column="code">
                {t('mapping.codes')}
              </SortableCell>
              <SortableCell sort={sort} column="status">
                {t('field.status')}
              </SortableCell>
              <SortableCell sort={sort} column="productName">
                {t('mapping.appProduct')}
              </SortableCell>
              {canManage ? <TableCell align="right">{t('common.actions')}</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={canManage ? 5 : 4} />
            ) : (
              rows.map((one) => (
                <TableRow key={one.id} hover>
                  <TableCell>
                    {one.shopName}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      #{one.shopProductId}
                      {one.shopVariantId !== null ? ` / ${one.shopVariantId}` : ''}
                      {one.shopExpiryDate
                        ? ` · ${t('batch.expires', { date: formatDate(one.shopExpiryDate, language) })}`
                        : ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {[one.shopEan, one.shopReference].filter(Boolean).join(' · ') || t('common.none')}
                  </TableCell>
                  <TableCell>
                    {t(`mappingStatus.${one.status}`)}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {one.status === 'MATCHED' && one.matchedBy
                        ? t(`matchMethod.${one.matchedBy}`)
                        : one.unmatchedReason
                          ? t(`unmatchedReason.${one.unmatchedReason}`)
                          : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{one.productName ?? t('common.none')}</TableCell>
                  {canManage ? (
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => setMapping(one)}>
                          {one.status === 'MATCHED' ? t('mapping.change') : t('mapping.map')}
                        </Button>
                        {one.status === 'IGNORED' || one.status === 'MATCHED' ? (
                          <Button
                            size="small"
                            color="inherit"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                companyId: link.companyId,
                                linkId: link.id,
                                mappingId: one.id,
                                decision: { action: 'UNMAP' },
                              })
                            }
                          >
                            {t('mapping.unmap')}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="inherit"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                companyId: link.companyId,
                                linkId: link.id,
                                mappingId: one.id,
                                decision: { action: 'IGNORE' },
                              })
                            }
                          >
                            {t('mapping.ignore')}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? (
        <EmptyState title={t('mapping.empty')} detail={t('mapping.emptyDetail')} />
      ) : null}
      {mapping !== null ? (
        <MappingDialog
          companyId={link.companyId}
          mapping={mapping}
          onClose={() => setMapping(null)}
          isBusy={decide.isPending}
          error={decide.error}
          onSubmit={(decision) =>
            decide.mutate({ companyId: link.companyId, linkId: link.id, mappingId: mapping.id, decision })
          }
        />
      ) : null}
    </Paper>
  );
};
